import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Field, PageHeader, Section } from "@/components/common/kit";
import { DateRangeFilter, ProductSelect, SalesmanSelect, useRange } from "@/components/common/Filters";
import RemoteTable from "@/components/common/RemoteTable";

export const ADJ_COLS = [
  { key: "adjustment_no", label: "Adj. ID" }, { key: "date", label: "Date", type: "date" }, { key: "salesman_name", label: "Salesman" },
  { key: "product_name", label: "Product" }, { key: "type", label: "Type" }, { key: "signed_qty", label: "Qty (±)", type: "int" },
  { key: "reason", label: "Reason" }, { key: "remarks", label: "Remarks" }, { key: "recorded_by", label: "Recorded by" },
];
const TYPES = [["SHORTAGE", "Shortage"], ["DAMAGED", "Damaged"], ["LOST", "Lost"], ["OTHER", "Other (reduce)"], ["EXCESS", "Excess found (add)"]];

function AdjForm({ open, onClose, onSaved }) {
  const { bizDate } = useAuth();
  const [f, setF] = useState({ salesman_id: "", product_id: "", type: "SHORTAGE", qty: "", reason: "", remarks: "", date: bizDate });
  const { data: stock } = useFetch(f.salesman_id ? "/stock/current" : null, { salesman_id: f.salesman_id });
  const cur = stock?.items.find((i) => i.product_id === f.product_id);
  const save = async () => {
    try {
      await api.post("/adjustments", { ...f, qty: Number(f.qty) });
      toast.success("Adjustment recorded");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="adj-form-dialog">
        <DialogHeader><DialogTitle>Record stock adjustment</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Salesman"><SalesmanSelect value={f.salesman_id} onChange={(v) => setF({ ...f, salesman_id: v })} className="w-full" testid="adj-salesman" /></Field>
          <Field label="Product" hint={cur ? `System stock: ${cur.qty} packs` : ""}><ProductSelect value={f.product_id} onChange={(v) => setF({ ...f, product_id: v })} products={stock?.items || []} testid="adj-product" /></Field>
          <Field label="Type">
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger data-testid="adj-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Quantity (packs)"><Input type="number" min={1} value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="adj-qty" /></Field>
          <Field label="Date"><Input type="date" value={f.date} max={bizDate} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="adj-date" /></Field>
          <Field label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="adj-reason" /></Field>
          <div className="sm:col-span-2"><Field label="Remarks"><Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="adj-remarks" /></Field></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="adj-save-button">Record & approve</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Adjustments() {
  const [range, setRange] = useRange("month");
  const [sid, setSid] = useState("");
  const [open, setOpen] = useState(false);
  const [rk, setRk] = useState(0);
  return (
    <div className="space-y-6">
      <PageHeader title="Stock Adjustments" subtitle="Shortage, damage, loss and other discrepancies — recorded and approved by Admin"
        actions={<Button onClick={() => setOpen(true)} data-testid="new-adjustment-button"><Plus className="mr-1.5 h-4 w-4" /> Record adjustment</Button>} />
      <div className="flex flex-wrap gap-2"><DateRangeFilter value={range} onChange={setRange} /><SalesmanSelect value={sid} onChange={setSid} allowAll /></div>
      <Section>
        <RemoteTable url="/adjustments" params={{ salesman_id: sid || undefined, date_from: range.from, date_to: range.to }} reloadKey={rk} columns={ADJ_COLS} title="Stock Adjustments" exportable testid="adjustments-table" />
      </Section>
      {open && <AdjForm open onClose={() => setOpen(false)} onSaved={() => { setOpen(false); setRk(rk + 1); }} />}
    </div>
  );
}
